import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { ListChecks, School, Users, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const InfoCards: React.FC = () => {
  const { t } = useTranslation('public');
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Gunakan useMemo agar data icon tetap rapi namun teks menjadi dinamis
  const cardData = useMemo(() => [
    {
      icon: <ListChecks className="w-10 h-10 text-undiksha-blue dark:text-undiksha-light" />,
      title: t('info_cards.tracks.title'),
      description: t('info_cards.tracks.desc'),
      link: "#conference-tracks",
    },
    {
      icon: <School className="w-10 h-10 text-undiksha-blue dark:text-undiksha-light" />,
      title: t('info_cards.partners.title'),
      description: t('info_cards.partners.desc'),
      link: "#university-partners",
    },
    {
      icon: <Users className="w-10 h-10 text-undiksha-blue dark:text-undiksha-light" />,
      title: t('info_cards.convention.title'),
      description: t('info_cards.convention.desc'),
      link: "registration/convention",
    },
  ], [t]);

  const handleMenuClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();

    // Jika tujuannya halaman lain (bukan hash)
    if (!href.startsWith("#")) {
      navigate(href.startsWith("/") ? href : `/${href}`);
      return;
    }

    // Jika saat ini bukan di halaman utama, arahkan ke root lalu scroll
    if (location.pathname !== "/") {
      navigate(`/${href}`);
      return;
    }

    // Scroll halus ke elemen yang dituju
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
      // Ubah URL tanpa reload
      window.history.replaceState(null, "", href);
    }
  };

  return (
    <section className="bg-white dark:bg-gray-900 pt-6 md:pt-10 relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cardData.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col border border-gray-100 dark:border-gray-700"
            >
              <div className="mb-6">{card.icon}</div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {card.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 flex-grow">
                {card.description}
              </p>
              <a
                href={card.link}
                onClick={(e) => handleMenuClick(e, card.link)}
                className="group font-semibold text-undiksha-blue dark:text-undiksha-light inline-flex items-center cursor-pointer"
              >
                {t('info_cards.read_more')}
                <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
      {/* TAGLINE / BANNER SECTION */}
      <div className="w-full bg-gradient-to-br from-blue-700 to-blue-900 text-white py-20 px-6 text-center mt-10">
        <h3 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
          {t('tagline.title')}
        </h3>
        <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
          {t('tagline.main')}
        </p>
        <p className="mt-4 text-lg md:text-xl italic opacity-90">
          {t('tagline.sub')}
        </p>
      </div>
    </section>
  );
};

export default InfoCards;